
import { useEffect, useRef } from 'react';
import Button from '@/components/base/Button';

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const scrolled = window.pageYOffset;
        const parallaxSpeed = scrolled * 0.5;
        const opacity = 1 - scrolled / (window.innerHeight * 0.8);
        
        heroRef.current.style.transform = `translateY(${parallaxSpeed}px)`;
        heroRef.current.style.opacity = Math.max(opacity, 0).toString();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section 
      ref={heroRef}
      id="home"
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat pt-16"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(147, 51, 234, 0.8)), url('https://readdy.ai/api/search-image?query=Modern%20business%20success%20visualization%20with%20multiple%20sales%20funnel%20graphics%2C%20conversion%20charts%20showing%20upward%20trends%2C%20laptop%20and%20tablet%20displaying%20marketing%20dashboards%2C%20professional%20workspace%20with%20clean%20white%20background%2C%20blue%20and%20purple%20gradient%20accents%2C%20high-converting%20funnel%20illustrations&width=1920&height=1080&seq=hero-bg-cofunnels-001&orientation=landscape')`
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
      
      {/* Floating elements for parallax effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-4 h-4 bg-yellow-400 rounded-full animate-ping opacity-70"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-white rounded-full animate-pulse opacity-60"></div>
        <div className="absolute bottom-40 left-20 w-8 h-8 bg-blue-300 rounded-full animate-bounce opacity-50"></div>
        <div className="absolute bottom-60 right-10 w-3 h-3 bg-purple-300 rounded-full animate-pulse opacity-70"></div>
      </div>
      
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center text-white">
          <div className="mb-6 animate-fade-in-up">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-sm font-medium hover:bg-white/30 transition-all duration-300">
              <i className="ri-star-fill text-yellow-400 mr-2 animate-spin-slow"></i>
              500+ Five-Star Reviews
            </span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight animate-fade-in-up delay-200">
            Funnels that
            <span className="block text-yellow-400 mt-2 animate-pulse">Convert</span>
          </h1>
          
          <p className="text-xl sm:text-2xl mb-8 max-w-4xl mx-auto leading-relaxed text-gray-100 animate-fade-in-up delay-400">
            Done-for-you sales funnels in ClickFunnels & GoHighLevel. Get high-converting funnels built by experts who've generated millions in revenue for clients worldwide.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 animate-fade-in-up delay-600">
            <Button variant="primary" size="lg" className="min-w-56 bg-yellow-500 hover:bg-yellow-600 text-black font-bold whitespace-nowrap transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
              <i className="ri-rocket-line mr-2 animate-bounce"></i>
              Get Your Funnel Now
            </Button>
            <Button variant="outline" size="lg" className="min-w-56 border-white text-white hover:bg-white hover:text-gray-900 whitespace-nowrap transform hover:scale-105 transition-all duration-300">
              <i className="ri-play-circle-line mr-2"></i>
              Watch Success Stories
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-200 animate-fade-in-up delay-800">
            <div className="flex items-center hover:text-white transition-colors duration-300">
              <i className="ri-shield-check-line text-green-400 mr-2 animate-pulse"></i>
              100% Money Back Guarantee
            </div>
            <div className="flex items-center hover:text-white transition-colors duration-300">
              <i className="ri-time-line text-blue-400 mr-2"></i>
              7-Day Delivery
            </div>
            <div className="flex items-center hover:text-white transition-colors duration-300">
              <i className="ri-customer-service-2-line text-purple-400 mr-2"></i>
              24/7 Support
            </div>
          </div>
        </div>
      </div>
      
      {/* Watermark */}
      <div className="absolute bottom-4 right-4 text-white/50 text-xs">
        Cofunnels.com
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
        <i className="ri-arrow-down-line text-2xl hover:text-yellow-400 transition-colors duration-300 cursor-pointer"></i>
      </div>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        .delay-200 { animation-delay: 0.2s; opacity: 0; }
        .delay-400 { animation-delay: 0.4s; opacity: 0; }
        .delay-600 { animation-delay: 0.6s; opacity: 0; }
        .delay-800 { animation-delay: 0.8s; opacity: 0; }
      `}</style>
    </section>
  );
}
